import { useRef, useState } from 'react';
import { Box } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';
import Quill from 'quill';

import * as graphql from '../../../../src/generated/graphql';
import { useLocalizedValue } from 'packages/kakapo-ui-application/hooks/useLocalizedState';
import { LocalizationScope } from 'packages/kakapo-ui-application/contexts/LocalizationScope';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import { QuillEditor } from './quill';
import { QuillToolbar } from './quill/QuillToolbar';
import { withFormik } from './Formik';
import Delta from 'quill-delta';

const Container = styled(Box)<{ focus?: boolean }>`
	border-radius: 6px;
	box-shadow: 0px 0px 0px 1px ${(props) => props.theme.colors.lightBlue};
	overflow: hidden;

	${(props) =>
		props.focus &&
		`
	outline: none;
	box-shadow: 0px 0px 0px 1px ${props.theme.colors.primary},
		0px 0px 0px 3px ${rgba(props.theme.colors.primary, 0.25)};
	`};
`;

const Editor = styled(Box)<{ focus?: boolean }>`
	box-shadow: 0px 1px 2px ${(props) => rgba(props.theme.colors.lightBlue, 0.5)} inset;
`;

export function QuillInput(props: {
	autoFocus?: boolean;
	value: graphql.Scalars['QuillDelta'];
	placeholder?: string;
	onChange: (value: graphql.Scalars['QuillDelta']) => void;
}) {
	const ref = useRef<Quill>();

	const [focus, setFocus] = useState(props.autoFocus);

	return (
		<Container focus={focus}>
			<QuillToolbar innerRef={ref} />
			<Editor focus={focus}>
				<QuillEditor
					autoFocus={props.autoFocus}
					p={3}
					value={props.value}
					onChange={props.onChange}
					onQuill={(quill) => (ref.current = quill)}
					onFocus={() => setFocus(true)}
					onBlur={() => setFocus(false)}
					placeholder={props.placeholder}
				/>
			</Editor>
		</Container>
	);
}

QuillInput.Formik = withFormik(QuillInput);

function LocalizableQuillInputInner(props: {
	autoFocus?: boolean;
	value: graphql.Scalars['LocalizedAttributedString'];
	placeholder?: string;
	onChange: (value: graphql.Scalars['LocalizedAttributedString']) => void;
}) {
	const ref = useRef<Quill>();

	const [focus, setFocus] = useState(props.autoFocus);
	const [value, setValue] = useLocalizedValue(props.value, props.onChange, new Delta());
	const { language } = useLocalize();

	return (
		<Container focus={focus}>
			<QuillToolbar innerRef={ref} localizable />
			<Editor focus={focus}>
				<QuillEditor
					key={language.id}
					autoFocus={props.autoFocus}
					p={3}
					value={value}
					onChange={setValue}
					onQuill={(quill) => (ref.current = quill)}
					onFocus={() => setFocus(true)}
					onBlur={() => setFocus(false)}
					placeholder={props.placeholder}
				/>
			</Editor>
		</Container>
	);
}

export function LocalizableQuillInput(props: {
	autoFocus?: boolean;
	value: graphql.Scalars['LocalizedAttributedString'];
	placeholder?: string;
	onChange: (value: graphql.Scalars['LocalizedAttributedString']) => void;
}) {
	return (
		<LocalizationScope>
			<LocalizableQuillInputInner {...props} />
		</LocalizationScope>
	);
}

QuillInput.Localizable = LocalizableQuillInput;

LocalizableQuillInput.Formik = withFormik(LocalizableQuillInput);
